// Super Admin Portal Route Configuration
import { lazy } from 'react';

// Lazy load Super Admin Portal components - Clean structure only
const SuperAdminDashboard = lazy(() => import('../../portals/super-admin/pages/Dashboard'));

const OrganizationManagement = lazy(() => import('../../portals/super-admin/pages/Organizations'));

const DepartmentManagement = lazy(() => import('../../portals/super-admin/pages/Departments'));

const RoleManagement = lazy(() => import('../../portals/super-admin/pages/Roles'));
const PermissionManagement = lazy(() => import('../../portals/super-admin/pages/Permissions'));
const UserManagement = lazy(() => import('../../portals/super-admin/pages/Users'));
const RBACGuide = lazy(() => import('../../portals/super-admin/pages/Guide'));

const TemplateBuilder = lazy(() => import('../../portals/super-admin/pages/TemplateBuilder'));

const RBACCleanup = lazy(() => import('../../portals/super-admin/pages/RBACCleanup'));

const HierarchyManagement = lazy(() => import('../../portals/super-admin/pages/Hierarchy'));

const RegionsManagement = lazy(() => import('../../portals/super-admin/pages/Regions'));

const TemplateManagement = lazy(
  () => import('../../portals/super-admin/pages/Templates/TemplateManagement')
);

const LLMDashboard = lazy(() => import('../../portals/super-admin/pages/LLM'));

const LLMConfiguration = lazy(() =>
  import('../../portals/super-admin/pages/LLM').then((module) => ({
    default: module.LLMConfiguration,
  }))
);

// const AuditLogs = lazy(() =>
//   import("../../portals/super-admin/pages/Audit")
// );

/**
 * Super Admin Portal Routes Configuration
 * For RBAC management and system oversight
 */
export const superAdminRoutes = {
  path: '/super-admin',
  name: 'Super Admin Portal',
  description: 'Portal for RBAC management and system oversight',
  requiredRoles: ['Super Admin'],
  requiredPermissions: ['super_admin_access'],
  children: [
    {
      index: true,
      component: SuperAdminDashboard,
      name: 'Dashboard',
      description: 'Super Admin system oversight dashboard',
      meta: {
        title: 'Super Admin Dashboard',
        breadcrumb: 'Dashboard',
        icon: 'admin_panel_settings',
      },
    },
    {
      path: 'hierarchy',
      component: HierarchyManagement,
      name: 'Hierarchy Overview',
      description: 'Complete organizational hierarchy view',
      meta: {
        title: 'Hierarchy Overview',
        breadcrumb: 'Hierarchy',
        icon: 'account_tree',
      },
    },
    {
      path: 'organizations',
      component: OrganizationManagement,
      name: 'Organizations',
      description: 'Manage organizations',
      meta: {
        title: 'Organization Management',
        breadcrumb: 'Organizations',
        icon: 'business',
      },
    },
    {
      path: 'regions',
      component: RegionsManagement,
      name: 'Regions/Branches/Depots',
      description: 'Manage regions, branches and depots',
      meta: {
        title: 'Regions/Branches/Depots Management',
        breadcrumb: 'Regions',
        icon: 'location_city',
      },
    },
    {
      path: 'departments',
      component: DepartmentManagement,
      name: 'Departments',
      description: 'Manage departments within organizations',
      meta: {
        title: 'Department Management',
        breadcrumb: 'Departments',
        icon: 'corporate_fare',
      },
    },
    {
      path: 'roles',
      component: RoleManagement,
      name: 'Roles',
      description: 'Manage roles and permissions',
      meta: {
        title: 'Role Management',
        breadcrumb: 'Roles',
        icon: 'security',
      },
    },
    {
      path: 'permissions',
      component: PermissionManagement,
      name: 'Permissions',
      description: 'Manage system permissions',
      meta: {
        title: 'Permission Management',
        breadcrumb: 'Permissions',
        icon: 'lock',
      },
    },
    {
      path: 'rbac-cleanup',
      component: RBACCleanup,
      name: 'RBAC Cleanup',
      description: 'Clean up duplicate departments and roles',
      meta: {
        title: 'RBAC Duplicate Cleanup',
        breadcrumb: 'RBAC Cleanup',
        icon: 'cleaning_services',
      },
    },
    {
      path: 'users',
      component: UserManagement,
      name: 'Users',
      description: 'Manage system users',
      meta: {
        title: 'User Management',
        breadcrumb: 'Users',
        icon: 'people',
      },
    },
    {
      path: 'guide',
      component: RBACGuide,
      name: 'Setup Guide',
      description: 'RBAC setup guide and wizard',
      meta: {
        title: 'RBAC Setup Guide',
        breadcrumb: 'Guide',
        icon: 'help_outline',
      },
    },
    {
      path: 'templates',
      component: TemplateBuilder,
      name: 'Template Builder',
      description: 'Create and manage onboarding templates for admin portal',
      meta: {
        title: 'Onboarding Template Builder',
        breadcrumb: 'Templates',
        icon: 'build_circle',
      },
    },
    {
      path: 'template-management',
      component: TemplateManagement,
      name: 'Template Management',
      description: 'Manage RBAC templates for departments and roles',
      meta: {
        title: 'RBAC Template Management',
        breadcrumb: 'Template Management',
        icon: 'view_module',
      },
    },
    {
      path: 'llm',
      component: LLMDashboard,
      name: 'LLM Dashboard',
      description: 'Semantic cache and LLM monitoring dashboard',
      meta: {
        title: 'LLM Dashboard',
        breadcrumb: 'LLM',
        icon: 'psychology',
      },
    },
    {
      path: 'llm-config',
      component: LLMConfiguration,
      name: 'LLM Configuration',
      description: 'Configure LLM providers and models',
      meta: {
        title: 'LLM Configuration',
        breadcrumb: 'Configuration',
        icon: 'settings',
      },
    },
  ],
};

export default superAdminRoutes;
